import { availabilityType, CloseDateType, OpenDateType } from '../types/availability.types'
import { areDateRangesOverlapping, timestampToDateString, timestampToDayjs } from '../../../../utils/dateTime.utils'
import { DeleteIcon } from '../../../../components/styles/Icons.styled'
import { theme } from '../../../../theme'
import { Box, IconButton } from '@mui/material'
import { Timestamp } from 'firebase/firestore'
import { PricingListType } from '../../PricingConfiguration/types/pricingConfiguration.types'
// ------------------------------------------ Check Date Range Overlap ------------------------------------------
export const doDateRangesOverlap = (range1: { startDate: Timestamp; endDate: Timestamp }, range2: { startDate: Timestamp; endDate: Timestamp }) => {
  return areDateRangesOverlapping(range1, range2)
}

// ------------------------------------------ Find Mismatches ------------------------------------------
export const findMismatches = (openDates: OpenDateType[], pricingLists: PricingListType[] | undefined) => {
  if (!pricingLists || !openDates) return { openDaysWithNoPriceLists: [], priceListsWithNoOpenDays: [] }

  const openDaysWithNoPriceLists: OpenDateType[] = []

  openDates.forEach((openDate) => {
    // Get all pricing lists that overlap with this open date range
    const overlappingPriceLists = pricingLists.filter((priceList) =>
      doDateRangesOverlap(openDate, { startDate: priceList.startDate, endDate: priceList.endDate })
    )

    // If no pricing lists overlap at all, add the entire range
    if (overlappingPriceLists.length === 0) {
      openDaysWithNoPriceLists.push(openDate)
      return
    }

    // Sort overlapping lists by start date
    overlappingPriceLists.sort((a, b) => a.startDate.toDate().getTime() - b.startDate.toDate().getTime())

    // Check for gaps in coverage
    let currentDate = timestampToDayjs(openDate.startDate)
    const endDate = timestampToDayjs(openDate.endDate)

    for (const priceList of overlappingPriceLists) {
      const priceStart = timestampToDayjs(priceList.startDate)
      const priceEnd = timestampToDayjs(priceList.endDate)

      // If there's a gap before this price list starts
      if (currentDate.isBefore(priceStart)) {
        openDaysWithNoPriceLists.push({
          startDate: Timestamp.fromDate(currentDate.toDate()),
          endDate: Timestamp.fromDate(priceStart.subtract(1, 'day').toDate()),
        })
      }

      // Update current date to the end of this price list
      currentDate = currentDate.isAfter(priceEnd) ? currentDate : priceEnd.add(1, 'day')
    }

    // If we haven't reached the end date, there's a gap at the end
    if (currentDate.isBefore(endDate)) {
      openDaysWithNoPriceLists.push({
        startDate: Timestamp.fromDate(currentDate.toDate()),
        endDate: openDate.endDate,
      })
    }
  })

  const priceListsWithNoOpenDays = pricingLists.filter(
    (priceList) => !openDates.some((openDate) => doDateRangesOverlap(openDate, { startDate: priceList.startDate, endDate: priceList.endDate }))
  )

  return { openDaysWithNoPriceLists, priceListsWithNoOpenDays }
}

// ------------------------------------------ Normalize Availability Dates ------------------------------------------
const normalizeAvailability = (openDates: OpenDateType[], closeDates: CloseDateType[]): availabilityType[] => {
  const normalizedOpenDates = openDates.map((openDate) => ({
    status: 'open' as const,
    stringDates: `${timestampToDateString(openDate.startDate)} - ${timestampToDateString(openDate.endDate)}`,
    openDate,
  }))
  const normalizedCloseDates = closeDates.map((closeDate) => ({
    status: 'close' as const,
    stringDates: timestampToDateString(closeDate),
    closeDate,
  }))
  return [...normalizedOpenDates, ...normalizedCloseDates]
}

// ------------------------------------------ Availability Table Data ------------------------------------------
export const availabilityTableData = (
  openDates: OpenDateType[],
  closeDates: CloseDateType[],
  t: (key: string) => string,
  handleOpenDateDelete: (openDate: OpenDateType) => void,
  handleCloseDateDelete: (closeDate: CloseDateType) => void
) => {
  const normalizedAvailability = normalizeAvailability(openDates, closeDates)
  return {
    headers: [t('availabilityConfiguration.table.header.status'), t('availabilityConfiguration.table.header.dates'), ''],
    renderRow: (availability: availabilityType) => {
      if (availability.status === 'open') {
        return [
          <Box component="span" style={{ color: theme.palette.success.main }} key={availability.stringDates}>
            {t('availabilityConfiguration.table.status.open')}
          </Box>,
          availability.stringDates,
          <IconButton key={availability.stringDates} onClick={() => availability.openDate && handleOpenDateDelete(availability.openDate)}>
            <DeleteIcon fontSize="small" />
          </IconButton>,
        ]
      }
      return [
        <Box component="span" style={{ color: theme.palette.error.main }} key={availability.stringDates}>
          {t('availabilityConfiguration.table.status.close')}
        </Box>,
        availability.stringDates,
        <IconButton key={availability.stringDates} onClick={() => availability.closeDate && handleCloseDateDelete(availability.closeDate)}>
          <DeleteIcon fontSize="small" />
        </IconButton>,
      ]
    },
    data: normalizedAvailability,
    getRowId: (availability: availabilityType) => availability.stringDates,
  }
}
